import React from "react";
import Link from "next/link";
import cls from "./productCard.module.scss";
import { Product } from "interfaces";
import getImage from "utils/getImage";
import Price from "components/price/price";
import Badge from "components/badge/badge";
import FallbackImage from "components/fallbackImage/fallbackImage";
import { Rating } from "@mui/material";
import StarFillIcon from "remixicon-react/StarFillIcon";
import StarLineIcon from "remixicon-react/StarLineIcon";
import ProductCardCounterContainer from "containers/productCardCounterContainer/productCardCounterContainer";
import useLiked from "hooks/useLiked";
import Heart3FillIcon from "remixicon-react/Heart3FillIcon";
import Heart3LineIcon from "remixicon-react/Heart3LineIcon";

type Props = {
  data: Product;
};

export default function ProductCard({ data }: Props) {
  const { isLiked, handleLikeOrDislike } = useLiked({
    type: "product",
    itemId: data?.id,
  });

  const totalPrice =
    data?.stock?.total_price || data?.stocks?.[0]?.total_price || 0;

  return (
    <Link
      href={`/product/${data?.uuid}`}
      scroll
      shallow
      className={cls.wrapper}
    >
      <button
        type="button"
        className={cls.likeButton}
        onClick={(e) => {
          e.preventDefault();
          handleLikeOrDislike();
        }}
      >
        {isLiked ? (
          <span className={cls.dislikeIcon}>
            <Heart3FillIcon size={24} />
          </span>
        ) : (
          <span className={cls.likeIcon}>
            <Heart3LineIcon size={24} />
          </span>
        )}
      </button>
      <div className={cls.addToCart}>
        <ProductCardCounterContainer data={data} />
      </div>
      <div className={cls.badgeContainer}>
        {!!(data?.stock?.discount || data?.discounts?.length) && (
          <div className={cls.badge}>
            <Badge type="discount" variant="circle" size="medium" />
          </div>
        )}
        {!!(data?.stock?.bonus || data?.stocks?.[0]?.bonus) && (
          <div className={cls.badge}>
            <Badge type="bonus" variant="circle" size="medium" />
          </div>
        )}
      </div>
      <div className={cls.imageContainer}>
        <FallbackImage
          fill
          src={getImage(data?.img)}
          alt={data?.translation?.title}
          quality={90}
          sizes="200px"
        />
      </div>
      <div className={cls.price}>
        <Price number={totalPrice} />
      </div>
      <h3 className={cls.title}>{data?.translation?.title}</h3>
      <div className={cls.review}>
        <div className={cls.rating}>
          <Rating
            value={data?.r_avg ?? 0}
            readOnly
            color="var(--yellow-rating)"
            icon={<StarFillIcon size={14} />}
            emptyIcon={<StarLineIcon size={14} />}
          />
        </div>
        <div className={cls.reviewCount}>{data?.r_count ?? 0}</div>
      </div>
    </Link>
  );
}
